// Pagină de înscriere self-service.
// Accesibilă doar prin link direct / cod QR (nu apare în meniuri).
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Baby, Eye, EyeOff, Plus, Trash2, UserPlus, Waves } from 'lucide-react';
import api from '../../lib/api';
import { TEMP_SIGNUP_KEY } from '../../lib/tempSignup';

const inputCls =
  'w-full bg-[#0A0A0A] border border-white/10 rounded p-4 text-base text-white focus:border-[#6db025] outline-none transition-colors placeholder:text-white/20';

const TempSignupPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', confirm: '' });
  const [isSwimmer, setIsSwimmer] = useState(false);
  const [children, setChildren] = useState([{ name: '', birth_date: '' }]);
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const setField = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const setChild = (idx, key, value) =>
    setChildren((prev) => prev.map((c, i) => (i === idx ? { ...c, [key]: value } : c)));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error('Parola trebuie să aibă cel puțin 6 caractere');
      return;
    }
    if (form.password !== form.confirm) {
      toast.error('Parolele nu coincid');
      return;
    }
    setSaving(true);
    try {
      const res = await api.post(`/temp-signup/${TEMP_SIGNUP_KEY}`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        password: form.password,
        is_swimmer: isSwimmer,
        children: children
          .filter((c) => c.name.trim())
          .map((c) => ({ name: c.name.trim(), birth_date: c.birth_date || null })),
      });
      localStorage.setItem('nautica_token', res.data.token);
      setDone(true);
      toast.success('Cont creat cu succes!');
      // Reîncărcăm aplicația logați direct pe pagina cu codurile QR
      setTimeout(() => { window.location.href = '/portal/qr'; }, 900);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la crearea contului');
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center gap-4 px-6 text-center">
        <div className="w-16 h-16 rounded-full bg-[#6db025]/10 border border-[#6db025]/30 flex items-center justify-center">
          <Waves size={28} className="text-[#6db025]" />
        </div>
        <h1 className="text-white font-heading text-2xl font-bold uppercase tracking-widest">Cont creat!</h1>
        <p className="text-white/40 text-sm">Ți-am trimis pe email un mesaj de bun venit și codurile QR de acces.</p>
        <p className="text-white/40 text-sm">Te ducem la codurile tale QR...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-8 px-4">
      <div className="max-w-md mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="w-14 h-14 mx-auto rounded-full bg-[#6db025]/10 border border-[#6db025]/30 flex items-center justify-center">
            <UserPlus size={24} className="text-[#6db025]" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-white uppercase tracking-widest">
            Creare Cont Nautica
          </h1>
          <p className="text-white/40 text-sm leading-relaxed">
            Completează datele tale și ale copiilor. După creare vei vedea direct codurile QR de acces.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Date părinte */}
          <div className="bg-[#0F0F0F] border border-white/5 rounded p-4 space-y-3">
            <p className="text-white/30 text-[11px] font-black uppercase tracking-widest">Datele tale (părinte)</p>
            <input type="text" required placeholder="Nume complet" value={form.name} onChange={setField('name')} className={inputCls} />
            <input type="email" required placeholder="Email" value={form.email} onChange={setField('email')} className={inputCls} autoComplete="email" />
            <input type="tel" placeholder="Telefon (opțional)" value={form.phone} onChange={setField('phone')} className={inputCls} autoComplete="tel" />

            <label className="flex items-start gap-3 p-3 bg-black/30 border border-white/5 rounded cursor-pointer hover:border-[#6db025]/30 transition-colors">
              <input
                type="checkbox"
                checked={isSwimmer}
                onChange={(e) => setIsSwimmer(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#6db025] flex-shrink-0"
              />
              <span>
                <span className="block text-white text-sm font-bold">Particip și eu la cursuri de înot</span>
                <span className="block text-white/30 text-[11px] mt-0.5">
                  Bifează doar dacă tu (adultul) vei înota. Vei primi și tu un cod QR de acces pe email.
                </span>
              </span>
            </label>
          </div>

          {/* Parola */}
          <div className="bg-[#0F0F0F] border border-white/5 rounded p-4 space-y-3">
            <p className="text-white/30 text-[11px] font-black uppercase tracking-widest">Parola contului</p>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                required
                placeholder="Parolă (min. 6 caractere)"
                value={form.password}
                onChange={setField('password')}
                className={`${inputCls} pr-12`}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white p-1"
                aria-label="Arată parola"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <input
              type={showPassword ? 'text' : 'password'}
              required
              placeholder="Confirmă parola"
              value={form.confirm}
              onChange={setField('confirm')}
              className={inputCls}
              autoComplete="new-password"
            />
          </div>

          {/* Copii */}
          <div className="bg-[#0F0F0F] border border-white/5 rounded p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-white/30 text-[11px] font-black uppercase tracking-widest flex items-center gap-2">
                <Baby size={14} className="text-purple-400" /> Copii înscriși
              </p>
              <button
                type="button"
                onClick={() => setChildren((prev) => [...prev, { name: '', birth_date: '' }])}
                className="flex items-center gap-1.5 text-[#6db025] text-[11px] font-black uppercase tracking-widest px-2 py-1.5 bg-[#6db025]/10 border border-[#6db025]/20 rounded hover:bg-[#6db025]/20 transition-colors"
              >
                <Plus size={13} /> Adaugă
              </button>
            </div>

            {children.map((child, idx) => (
              <div key={idx} className="space-y-2 p-3 bg-black/30 border border-white/5 rounded">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder={`Numele copilului ${children.length > 1 ? idx + 1 : ''}`.trim()}
                    value={child.name}
                    onChange={(e) => setChild(idx, 'name', e.target.value)}
                    className={inputCls}
                  />
                  {children.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setChildren((prev) => prev.filter((_, i) => i !== idx))}
                      className="px-3 text-red-500/50 hover:text-red-500 hover:bg-red-500/10 transition-colors rounded flex-shrink-0"
                      aria-label="Șterge copilul"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
                <div className="space-y-1">
                  <label className="text-white/20 text-[10px] font-black uppercase tracking-widest pl-1">
                    Data nașterii (opțional)
                  </label>
                  <input
                    type="date"
                    value={child.birth_date}
                    onChange={(e) => setChild(idx, 'birth_date', e.target.value)}
                    className={inputCls}
                  />
                </div>
              </div>
            ))}
            <p className="text-white/20 text-[11px]">
              Dacă nu ai copii de înscris, lasă secțiunea goală - poți participa tu ca adult.
            </p>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full bg-[#6db025] hover:bg-[#7ec82b] text-black py-4 rounded uppercase font-black tracking-widest text-sm disabled:opacity-50 transition-colors"
          >
            {saving ? 'Se creează contul...' : 'Creează contul'}
          </button>

          <p className="text-center text-white/30 text-sm">
            Ai deja cont?{' '}
            <button type="button" onClick={() => navigate('/login')} className="text-[#6db025] font-bold hover:underline">
              Loghează-te
            </button>
          </p>
        </form>
      </div>
    </div>
  );
};

export default TempSignupPage;
